import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import PropTypes from 'prop-types'
import {
  Avatar,
  Dropdown,
  Empty,
  Menu,
  message,
  Modal,
  Pagination,
  Tooltip,
} from 'antd'
import { inject, observer } from 'mobx-react'
import DashboardLayout from '../../layouts/DashboardLayout'
import PageTitle from '../../components/PageTitle'
import ContentBlockWrapper from '../../components/ContentBlockWrapper'
import SpinnerInlineComponent from '../../components/SpinnerInlineComponent/SpinnerInlineComponent'
import { ACL_ACTION_TYPE, MESSAGE } from '../../constants'
import AccessControlAction from '../../components/AccessControlAction'
import {
  CalendarOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
  PaperClipOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import ExclamationCircleOutlined from '@ant-design/icons/lib/icons/ExclamationCircleOutlined'
import { EmptyText, WordBreak } from '../../components/Common/CellText'
import FileItem from '../../components/Common/FileItemComponent/FileItem'
import utils from '../../utils'
import queryString from 'query-string'
import {
  TinTucContentWrapper,
  TinTucCreateButton,
  TinTucDetailCard,
  TinTucDetailShell,
  TinTucList,
  TinTucListItem,
  TinTucListPanel,
  TinTucMasterDetailLayout,
  TinTucPageWrapper,
  TinTucPaginationWrapper,
  TinTucDropdownGlobalStyle,
} from './GridTinTucStyled'
import {
  getTinTucAuthorName,
  getTinTucFormattedDate,
  getTinTucInitialName,
  TIN_TUC_PAGE_SIZE,
} from './tinTucConstants'
import {
  getTinTucErrorMessage,
  logTinTucErrorInDev,
} from './tinTucErrorHelper'
import AddEditTinTucModalV2 from './AddEditNotificationModalV2'

const { confirm } = Modal

const TinTucPage = propsTinTucPage => {
  const { aclStore, loadingAnimationStore, location, match, tintucStore } =
    propsTinTucPage

  const latestRequestId = useRef(0)
  const readNewsIdsRef = useRef({})
  const { newsList, tinTucTotalCount } = tintucStore

  const [isLoadingNews, setIsLoadingNews] = useState(false)
  const [selectedKey, setSelectedKey] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingNotificationId, setEditingNotificationId] = useState(null)

  const selectedNews = useMemo(
    () => newsList.find(item => String(item.id) === selectedKey),
    [newsList, selectedKey]
  )

  // Nghiệp vụ: link cũ `/view/:id` hoặc dashboard `?selectedId=` đều mở lại trang danh sách và chọn đúng thông báo.
  const selectedIdFromRoute = useMemo(() => {
    const queryParams = queryString.parse(location?.search || '')
    return match?.params?.id || queryParams.selectedId
  }, [location, match])

  // Nghiệp vụ: tải đúng page hiện tại, thay dữ liệu list thay vì append để phân trang không bị trùng item.
  const loadNews = useCallback(
    async ({ page = 0 } = {}) => {
      const requestId = latestRequestId.current + 1
      latestRequestId.current = requestId

      try {
        setIsLoadingNews(true)
        tintucStore.setTintucQueryParams({
          page,
          size: TIN_TUC_PAGE_SIZE,
        })
        const response = await tintucStore.getAllNews({ replace: true })

        if (latestRequestId.current !== requestId) return response

        // Nghiệp vụ: ưu tiên bôi xanh thông báo được truyền từ dashboard/link cũ, nếu không có thì chọn item đầu tiên.
        const responseData = response.data?.data || []
        const selectedItem = selectedIdFromRoute
          ? responseData.find(
              item => String(item.id) === String(selectedIdFromRoute)
            )
          : null
        const firstNews = selectedItem || responseData[0]
        setSelectedKey(firstNews?.id ? String(firstNews.id) : null)
        return response
      } catch (err) {
        logTinTucErrorInDev(err, 'TinTucPage.loadNews')
        message.error(
          getTinTucErrorMessage(err, 'Không thể tải danh sách thông báo!')
        )
        return undefined
      } finally {
        if (latestRequestId.current === requestId) {
          setIsLoadingNews(false)
        }
      }
    },
    [selectedIdFromRoute, tintucStore]
  )

  useEffect(() => {
    ;(async () => {
      await loadNews()
    })()
    return () => {
      tintucStore.clear()
    }
  }, [loadNews, tintucStore])

  // Nghiệp vụ: khi thông báo đang được chọn thì ghi nhận đã đọc, tránh gọi lặp API cho cùng một item trong phiên hiện tại.
  useEffect(() => {
    if (!selectedNews?.id) return
    const readKey = String(selectedNews.id)
    if (readNewsIdsRef.current[readKey]) return

    readNewsIdsRef.current[readKey] = true
    tintucStore
      .createCurrentUserReadNews({
        newsId: selectedNews.id,
      })
      .catch(error =>
        logTinTucErrorInDev(error, 'TinTucPage.createCurrentUserReadNews')
      )
  }, [selectedNews, tintucStore])

  const onItemClick = useCallback(news => {
    setSelectedKey(news?.id ? String(news.id) : null)
  }, [])

  // Nghiệp vụ: tạo mới dùng modal tại trang danh sách, không điều hướng sang page create cũ.
  const onClickCreate = useCallback(() => {
    setEditingNotificationId(null)
    setIsModalOpen(true)
  }, [])

  // Nghiệp vụ: sửa thông báo ngay trong modal, vẫn giữ ACL edit__NEWS ở menu thao tác.
  const onClickEdit = useCallback(id => {
    setEditingNotificationId(id)
    setIsModalOpen(true)
  }, [])

  const onChangePagination = useCallback(
    page => {
      loadNews({ page: page - 1 })
    },
    [loadNews]
  )

  // Nghiệp vụ: xóa xong reload lại đúng page; nếu xóa item cuối của page thì lùi về page trước.
  const onClickDelete = useCallback(
    async id => {
      try {
        setIsDeleting(true)
        loadingAnimationStore.showSpinner(true)
        await tintucStore.delete(id)

        const isLastItemOnPage = newsList.length <= 1
        const currentPage = tintucStore.tintucQueryParams.page
        const nextPage =
          isLastItemOnPage && currentPage > 0 ? currentPage - 1 : currentPage

        await loadNews({ page: nextPage })
        message.success(MESSAGE.SUCCESS_NEWS)
      } catch (err) {
        logTinTucErrorInDev(err, 'TinTucPage.deleteNews')
        message.error(getTinTucErrorMessage(err, 'Không thể xóa thông báo!'))
      } finally {
        setIsDeleting(false)
        loadingAnimationStore.showSpinner(false)
      }
    },
    [loadNews, loadingAnimationStore, newsList.length, tintucStore]
  )

  const showDeleteConfirm = useCallback(
    id => {
      confirm({
        title: 'Bạn có chắc muốn xóa tin này ?',
        icon: <ExclamationCircleOutlined style={{ color: '#ef4444' }} />,
        okText: 'Xác nhận xóa',
        okType: 'danger',
        cancelText: 'Hủy',
        className: 'news-delete-confirm-modal',
        okButtonProps: {
          disabled: isDeleting,
        },
        onOk: () => onClickDelete(id),
        onCancel() {},
      })
    },
    [isDeleting, onClickDelete]
  )

  // Nghiệp vụ: chỉ hiển thị action sửa/xóa theo ACL; action xem chi tiết được ẩn để người dùng ở lại panel hiện tại.
  const menuControlNotification = useMemo(() => {
    if (!selectedNews?.id) return <Menu />

    return (
      <Menu>
        {aclStore.checkAccessControlAction(ACL_ACTION_TYPE.edit__NEWS) && (
          <Menu.Item
            key="edit"
            className="menu-item-edit"
            onClick={() => onClickEdit(selectedNews.id)}
            icon={<EditOutlined />}>
            Sửa thông tin
          </Menu.Item>
        )}
        {aclStore.checkAccessControlAction(ACL_ACTION_TYPE.delete__NEWS) && (
          <Menu.Item
            key="delete"
            className="menu-item-danger"
            onClick={() => showDeleteConfirm(selectedNews.id)}
            danger
            disabled={isDeleting}
            icon={<DeleteOutlined />}>
            Xóa
          </Menu.Item>
        )}
      </Menu>
    )
  }, [
    aclStore,
    isDeleting,
    onClickEdit,
    selectedNews,
    showDeleteConfirm,
  ])

  return (
    <DashboardLayout>
      <Helmet>
        <title>Thông báo chung</title>
      </Helmet>
      <TinTucDropdownGlobalStyle />
      <TinTucPageWrapper>
        <AddEditTinTucModalV2
          open={isModalOpen}
          setOpen={setIsModalOpen}
          selectedNotificationId={editingNotificationId}
          onRefresh={async () => {
            await loadNews()
            if (editingNotificationId) {
              setSelectedKey(String(editingNotificationId))
            }
          }}
        />
        <PageTitle
          location={location}
          title="Thông báo chung"
          customBreadcrumb={
            <span className="news-page-heading">Thông báo chung</span>
          }>
          <AccessControlAction aclActionType={ACL_ACTION_TYPE.create__NEWS}>
            <TinTucCreateButton
              icon={<PlusOutlined />}
              onClick={onClickCreate}
              type={'primary'}>
              Đăng thông báo
            </TinTucCreateButton>
          </AccessControlAction>
        </PageTitle>

        <TinTucContentWrapper>
          {isLoadingNews && newsList.length === 0 ? (
            <ContentBlockWrapper style={{ height: 400, position: 'relative' }}>
              <SpinnerInlineComponent
                sizeSpin={'small'}
                isLoading={true}
                noBackground
                tip={'Đang tải...'}
              />
            </ContentBlockWrapper>
          ) : (
            <>
              {newsList.length === 0 ? (
                <Empty description="Không có thông báo chung" />
              ) : (
                <TinTucMasterDetailLayout>
                  <TinTucListPanel>
                    <TinTucList>
                      {newsList.map(item => {
                        const itemDate = getTinTucFormattedDate(item)
                        const isSelected = String(item.id) === selectedKey

                        return (
                          <Tooltip title={item.subject} key={String(item.id)}>
                            <TinTucListItem
                              type="button"
                              className={isSelected ? 'active' : ''}
                              onClick={() => onItemClick(item)}>
                              <Avatar className="news-list-avatar">
                                {getTinTucInitialName(item)}
                              </Avatar>
                              <span className="news-list-text">
                                <span className="news-list-top-row">
                                  <span className="news-list-author">
                                    {getTinTucAuthorName(item)}
                                  </span>
                                  {itemDate && (
                                    <span className="news-list-date">
                                      {itemDate}
                                    </span>
                                  )}
                                </span>
                                <span className="news-list-subject">
                                  {item.subject}
                                </span>
                              </span>
                            </TinTucListItem>
                          </Tooltip>
                        )
                      })}
                    </TinTucList>
                  </TinTucListPanel>

                  <TinTucDetailShell>
                    <TinTucDetailCard>
                      {selectedNews ? (
                        <>
                          <div className="news-detail-header-fixed">
                            <div className="news-detail-header">
                              <div className="news-detail-header-left">
                                <h2 className="news-detail-subject">
                                  {selectedNews.subject}
                                </h2>
                                <div className="news-detail-meta">
                                  <span className="news-detail-meta-item">
                                    <Avatar
                                      size={22}
                                      className="meta-author-avatar">
                                      {getTinTucInitialName(selectedNews)}
                                    </Avatar>
                                    <span className="meta-author-name">
                                      {getTinTucAuthorName(selectedNews)}
                                    </span>
                                  </span>
                                  {getTinTucFormattedDate(selectedNews) && (
                                    <span className="news-detail-meta-item">
                                      <CalendarOutlined />
                                      <span>
                                        {getTinTucFormattedDate(selectedNews)}
                                      </span>
                                    </span>
                                  )}
                                </div>
                              </div>
                              <Dropdown
                                overlay={menuControlNotification}
                                overlayClassName="news-action-dropdown"
                                placement="bottomRight"
                                trigger={['click']}>
                                <button
                                  type="button"
                                  className="news-detail-menu-btn"
                                  aria-label="Tùy chọn">
                                  <MoreOutlined />
                                </button>
                              </Dropdown>
                            </div>
                          </div>
                          <div className="news-detail-body">
                            <div className="news-detail-content">
                              {WordBreak(selectedNews.content, true)}
                            </div>
                            {selectedNews.attachments?.length > 0 && (
                              <div className="news-attachments">
                                <div className="news-attachments-header">
                                  <PaperClipOutlined />
                                  <span>Tài liệu đính kèm</span>
                                  <span className="attachments-count">
                                    {selectedNews.attachments.length}
                                  </span>
                                </div>
                                <div className="news-attachments-list">
                                  {selectedNews.attachments.map(file => (
                                    <FileItem
                                      key={file.file_id}
                                      file_id={file.file_id}
                                      file_name={file.file_name}
                                      file_type={utils.getExtensionFile(
                                        file.file_name
                                      )}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            flex: 1,
                            minHeight: '50vh',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                          <Empty description="Chưa chọn thông báo" />
                        </div>
                      )}
                    </TinTucDetailCard>
                  </TinTucDetailShell>
                </TinTucMasterDetailLayout>
              )}

              <TinTucPaginationWrapper>
                <Pagination
                  current={tintucStore.tintucQueryParams.page + 1}
                  disabled={isLoadingNews}
                  hideOnSinglePage
                  onChange={onChangePagination}
                  pageSize={TIN_TUC_PAGE_SIZE}
                  showLessItems
                  showSizeChanger={false}
                  total={tinTucTotalCount}
                />
              </TinTucPaginationWrapper>
            </>
          )}
        </TinTucContentWrapper>
      </TinTucPageWrapper>
    </DashboardLayout>
  )
}

TinTucPage.propTypes = {
  aclStore: PropTypes.object,
  loadingAnimationStore: PropTypes.object,
  location: PropTypes.object,
  match: PropTypes.object,
  tintucStore: PropTypes.object,
}

export default memo(
  inject(
    'tintucStore',
    'loadingAnimationStore',
    'authenticationStore',
    'commonStore',
    'aclStore',
  )(observer(TinTucPage))
)
